import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { tenantAdminApi } from '@/utils/tenantAdminApi';
import { IITH } from '@/pages/landing/iithBrand';
import StaffProfileHero from './StaffProfileHero';
import StaffProfileStats from './StaffProfileStats';
import StaffAppointmentsChart from './StaffAppointmentsChart';
import StaffProfileSections from './StaffProfileSections';

/** LinkedIn-style staff profile slide-over with activity summary. */
export default function StaffProfileView({ userId, roleLabel, open, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return undefined;
    let cancelled = false;
    setLoading(true);
    setData(null);
    tenantAdminApi.getUserProfile(userId, 90)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) toast.error('Could not load profile'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, userId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const user = data?.user;
  const summary = data?.summary;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="staff-profile-view">
      <button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-sm" aria-label="Close" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-3xl bg-[#E8EEF5] shadow-2xl flex flex-col overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/35 text-white hover:bg-black/50 grid place-items-center"
          aria-label="Close profile"
          data-testid="staff-profile-view-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex-1 overflow-y-auto">
          {loading || !user ? (
            <div className="flex items-center justify-center py-32 text-zinc-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading profile…
            </div>
          ) : (
            <>
              <div className="bg-white border-b shadow-sm">
                <StaffProfileHero
                  user={user}
                  roleLabel={roleLabel || user.role_name}
                  accent={IITH.saffron}
                  onEdit={onEdit ? () => onEdit(user) : undefined}
                />
              </div>
              <div className="px-6 py-5 space-y-5 max-w-3xl mx-auto">
                <StaffProfileStats summary={summary} />
                <StaffAppointmentsChart userId={userId} />
                <StaffProfileSections user={user} summary={summary} />
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
